import { lenient } from '@massilia/contracts'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import type { ChatTurnMeta, StreamChatRequest } from '../../types/index.js'

import { apiClient } from '../../lib/client.js'
import { chatKeys } from './keys.js'

export interface StreamChatMutationResult {
  content: string
  meta?: ChatTurnMeta
}

export interface StreamChatMutationVariables extends StreamChatRequest {
  onChunk?: (chunk: string) => void
  signal?: AbortSignal
}

/** Corps d'erreur du endpoint SSE, décodé avant d'atteindre l'écran. */
const streamErrorPayloadSchema = z.object({ message: lenient(z.string()) }).catch({})

const SSE_DATA_PREFIX = 'data:'
const META_PREFIX = '[META]'
const SSE_EVENT_SEPARATOR = '\n\n'

/** Les évènements SSE complets lus dans le tampon, et le reliquat encore partiel. */
interface SseExtraction {
  events: string[]
  remaining: string
}

export async function sendChatFeedback(
  traceId: string,
  value: number,
  comment?: string
): Promise<void> {
  await apiClient.client.post('/chat/feedback', { traceId, comment, value })
}

export function useStreamChatMutation() {
  return useMutation({
    mutationFn: streamChatMutationFn,
    mutationKey: chatKeys.stream,
  })
}

function extractCompleteSseEvents(buffer: string): SseExtraction {
  const events: string[] = []
  let start = 0

  while (true) {
    const separatorIdx = buffer.indexOf(SSE_EVENT_SEPARATOR, start)
    if (separatorIdx === -1) {
      break
    }
    events.push(buffer.slice(start, separatorIdx))
    start = separatorIdx + SSE_EVENT_SEPARATOR.length
  }

  return {
    events,
    remaining: buffer.slice(start),
  }
}

function extractSseData(line: string): null | string {
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return null
  }

  // SSE allows exactly one optional space after "data:".
  const raw = line.slice(SSE_DATA_PREFIX.length)
  return raw.startsWith(' ') ? raw.slice(1) : raw
}

function extractSseEventData(event: string): null | string {
  const lines = event.split('\n')
  const dataLines: string[] = []

  for (const line of lines) {
    const chunk = extractSseData(line)
    if (chunk === null) {
      continue
    }

    dataLines.push(chunk)
  }

  if (dataLines.length === 0) {
    return null
  }

  // SSE spec: an event with multiple `data:` lines joins them with "\n".
  return dataLines.join('\n')
}

function parseMetaChunk(chunk: string): ChatTurnMeta | undefined {
  try {
    const json = chunk.slice(META_PREFIX.length)
    return JSON.parse(json) as ChatTurnMeta
  } catch {
    return undefined
  }
}

async function streamChatMutationFn({
  clientState,
  messages,
  onChunk,
  signal,
}: StreamChatMutationVariables): Promise<StreamChatMutationResult> {
  const baseUrl = apiClient.client.defaults.baseURL ?? ''
  const response = await fetch(`${baseUrl}/chat/stream`, {
    body: JSON.stringify({
      clientState,
      messages,
    }),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    signal,
  })

  if (!response.ok) {
    let backendMessage: string | undefined

    try {
      backendMessage = streamErrorPayloadSchema.parse(await response.json()).message
    } catch {
      const fallbackText = await response.text().catch(() => '')
      if (fallbackText) {
        backendMessage = fallbackText
      }
    }

    if (response.status === 429) {
      throw new Error(backendMessage ?? 'Trop de messages envoyés. Veuillez patienter.')
    }

    throw new Error(backendMessage ?? 'Stream connection failed')
  }

  if (!response.body) {
    throw new Error('Stream connection failed')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let content = ''
  let meta: ChatTurnMeta | undefined
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true }).replaceAll('\r\n', '\n')
    const events = extractCompleteSseEvents(buffer)
    buffer = events.remaining

    for (const event of events.events) {
      const chunk = extractSseEventData(event)
      if (chunk === null || chunk === '[DONE]') {
        continue
      }

      if (chunk.startsWith(META_PREFIX)) {
        meta = parseMetaChunk(chunk)
        continue
      }

      // [LOADING], [MODE], [NEWMSG] are signals to the frontend, not content
      if (chunk === '[LOADING]' || chunk === '[NEWMSG]' || chunk.startsWith('[MODE]')) {
        onChunk?.(chunk)
        continue
      }

      content += chunk
      onChunk?.(chunk)
    }
  }

  if (buffer) {
    const chunk = extractSseEventData(buffer)
    if (chunk !== null && chunk !== '[DONE]') {
      if (chunk === '[LOADING]' || chunk === '[NEWMSG]' || chunk.startsWith('[MODE]')) {
        onChunk?.(chunk)
      } else if (chunk.startsWith(META_PREFIX)) {
        meta = parseMetaChunk(chunk)
      } else {
        content += chunk
        onChunk?.(chunk)
      }
    }
  }

  return { content, meta }
}
